import { Link } from "react-router-dom";
import {
  Briefcase,
  Upload,
  Brain,
  Send,
  Video,
  ClipboardCheck,
  Sparkles,
  RotateCcw,
  BookOpen,
  Mail,
  Users,
  Palette,
  GitCompare,
  TrendingUp,
  Bot,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const STEPS = [
  {
    n: "01",
    icon: Briefcase,
    title: "Create your first job",
    body: "Set the role title, paste the JD, and Hireloop auto-tailors 5-7 interview questions. You can always edit them, skip the warm-up, or add your own. Each job has its own public careers-page URL.",
    cta: { label: "Open Dashboard → Create Job", to: "/dashboard" },
    palette: "border-blue-200 bg-blue-50 text-blue-700",
  },
  {
    n: "02",
    icon: Upload,
    title: "Upload résumés (or share the careers link)",
    body: "Drag up to 100 résumés at once and Hireloop's AI extracts name, email, phone, work history, and skills. Want candidates to apply themselves? Share the public careers URL — they submit either a résumé OR a structured form.",
    cta: { label: "Drag-and-drop on any Job page", to: "/dashboard" },
    palette: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  {
    n: "03",
    icon: Brain,
    title: "Watch AI rank your top candidates",
    body: "Within seconds each résumé is scored 0-100 against the JD with a 2-sentence explanation. Use the score floor, skill filters, or Kanban view to focus on the top N. The bar chart on the Job page shows the score distribution at a glance.",
    cta: { label: "Tune scoring on any Job page", to: "/dashboard" },
    palette: "border-violet-200 bg-violet-50 text-violet-700",
  },
  {
    n: "04",
    icon: Send,
    title: "Invite the top N — now or scheduled",
    body: 'Pick "Top 5", choose "Email candidates now" or use "Schedule for Mon 9 AM", then click. Hireloop fires a branded Resend email with a unique async-interview link. Quick-pick chips (Tomorrow 8 AM · Mon 9 AM · In 1 week) make batch-scheduling fast.',
    cta: { label: "Use the Invite bar on any Job page", to: "/dashboard" },
    palette: "border-orange-200 bg-orange-50 text-orange-700",
  },
  {
    n: "05",
    icon: Video,
    title: "Run live or async interviews",
    body: "Candidates record async answers in their own time, OR you can spin up a 1:1 WebRTC live room with Cloudflare TURN. While live, the AI Co-pilot sidebar suggests follow-up questions and surfaces gaps in their answers in real time.",
    cta: { label: "View any candidate's recording", to: "/dashboard" },
    palette: "border-rose-200 bg-rose-50 text-rose-700",
  },
  {
    n: "06",
    icon: ClipboardCheck,
    title: "Score, decide, hire",
    body: "Each answer gets an AI score + transcript. Add private recruiter notes, generate a one-click PDF Candidate Brief for hiring managers, push to Greenhouse/Lever, and move candidates through your Kanban: New → Awaiting → Interviewed → Hired.",
    cta: { label: "Open any candidate review", to: "/dashboard" },
    palette: "border-indigo-200 bg-indigo-50 text-indigo-700",
  },
];

const DEEP_DIVES = [
  {
    icon: Mail,
    title: "Email engagement analytics",
    body: "Dashboard widget shows opens · clicks · bounces · response time per job. Sort applicants by who clicked the invite first.",
    to: "/dashboard",
  },
  {
    icon: Users,
    title: "Team seats & shared branding",
    body: "Invite teammates, set per-seat permissions, and share one company logo + reply-to email across the whole team.",
    to: "/team",
  },
  {
    icon: Palette,
    title: "Custom branding & careers page",
    body: "Replace the Hireloop logo, customize email templates, and publish a public /c/<your-id> careers page with all open roles.",
    to: "/settings",
  },
  {
    icon: GitCompare,
    title: "Head-to-head candidate comparison",
    body: "From Applicants, select 2-4 candidates and click Compare. See résumé scores, weighted scores, per-question winners (with 'Best' badges), AI gap analysis, and 'Open full review' side-by-side.",
    to: "/applicants",
  },
  {
    icon: TrendingUp,
    title: "Pipeline funnel analytics",
    body: "The Dashboard now ships with a 6-stage funnel (Applied → Invited → Opened → Started → Completed → Hired) with conversion KPIs, 7/30/90-day window, and per-role filter.",
    to: "/dashboard",
  },
  {
    icon: Bot,
    title: "AI Co-pilot — Ask the candidate",
    body: "Every completed Review page has a co-pilot panel. Ask 'What are this candidate's strengths?' or 'Give me 3 follow-up questions' and get answers grounded in the actual interview with Q-citations and a confidence pill.",
    to: "/applicants",
  },
  {
    icon: Calendar,
    title: "Schedule interviews on Google Calendar",
    body: "Connect Google Calendar in Settings, then book one-click interview holds from the Review page with Google Meet links and automatic candidate invites.",
    to: "/settings",
  },
];

export default function Learn() {
  const { user } = useAuth();
  const onReplayTour = () => {
    try {
      localStorage.removeItem("hireloop:onboarding-tour-done");
    } catch {
      /* ignore */
    }
    window.location.assign("/dashboard?tour=1");
  };

  return (
    <main className="min-h-screen bg-white" data-testid="learn-page">
      <header className="border-b border-slate-200 bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-blue-700">
            <BookOpen className="mr-2 inline h-3 w-3" />
            Hireloop Academy
          </p>
          <h1
            data-testid="learn-page-title"
            className="mt-4 max-w-3xl text-4xl font-bold leading-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            Six steps from upload to hire.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            New to Hireloop? Take the 90-second tour or scan the step-by-step
            below. Every section deep-links to the real screen so you can try
            as you read.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              data-testid="learn-start-tour-btn"
              onClick={onReplayTour}
              className="rounded-sm bg-blue-700 px-5 py-5 hover:bg-blue-600"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Start the 90-second interactive tour
            </Button>
            <Link to="/dashboard" data-testid="learn-skip-to-dashboard">
              <Button
                variant="outline"
                className="rounded-sm border-slate-300 px-5 py-5"
              >
                Skip to dashboard
              </Button>
            </Link>
          </div>
          {user?.name && (
            <p
              className="mt-6 text-sm text-slate-500"
              data-testid="learn-welcome-line"
            >
              Welcome aboard, {user.name.split(" ")[0]} — bookmark this page.
              You can replay the tour anytime.
            </p>
          )}
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <h2 className="text-2xl font-bold text-slate-900">
          The Hireloop loop
        </h2>
        <p className="mt-2 max-w-2xl text-slate-600">
          Most teams run this six-step loop hundreds of times per quarter.
          Click any card to jump into the relevant screen.
        </p>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {STEPS.map((s) => (
            <Link
              key={s.n}
              to={s.cta.to}
              data-testid={`learn-step-${s.n}`}
              className={`group flex gap-5 rounded-sm border ${s.palette.split(" ")[0]} bg-white p-6 transition-transform hover:-translate-y-0.5 hover:shadow-md`}
            >
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-sm ${s.palette}`}
              >
                <s.icon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400">
                  Step {s.n}
                </p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  {s.body}
                </p>
                <p className="mt-3 text-xs font-medium text-blue-700 group-hover:underline">
                  → {s.cta.label}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
          <h2 className="text-2xl font-bold text-slate-900">Deep dives</h2>
          <p className="mt-2 max-w-2xl text-slate-600">
            Smaller workflows that turn Hireloop from a tool into a system of
            record.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {DEEP_DIVES.map((d) => (
              <Link
                key={d.title}
                to={d.to}
                data-testid={`learn-deepdive-${d.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex flex-col rounded-sm border border-slate-200 bg-white p-5 transition-transform hover:-translate-y-0.5 hover:shadow"
              >
                <d.icon className="h-5 w-5 text-blue-700" />
                <h3 className="mt-3 text-base font-semibold text-slate-900">
                  {d.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {d.body}
                </p>
                <span className="mt-4 text-xs font-medium text-blue-700 group-hover:underline">
                  Open →
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-12 flex items-center justify-between gap-3 rounded-sm border border-slate-200 bg-white p-5">
            <p className="text-sm text-slate-700">
              Want the full reference?{" "}
              <Link
                to="/settings"
                data-testid="learn-open-manual-link"
                className="font-medium text-blue-700 hover:underline"
              >
                Open the searchable Employer Manual
              </Link>{" "}
              in Settings — 36 sections covering every workflow.
            </p>
            <button
              type="button"
              data-testid="learn-replay-tour-btn"
              onClick={onReplayTour}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="h-3 w-3" />
              Replay the tour
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
